import React, { useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";

import OverviewTab from "./OverviewTab";
import { DateTime } from "components/common/Date";
import { useUserOverview } from "./UserOverviewContext";
import Avatar from "components/common/Avatar";
import {
  isValidUserOverviewTabParam,
  userOverviewTabIdFromSearchParam,
} from "./overviewTabSearchParams";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";

const Section = () => {
  const { can } = useAccess();
  const canEditUser = can(Permissions.USER.UPDATE);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUserOverview();

  const tabQuery = searchParams.get("tab");
  const activeTab = userOverviewTabIdFromSearchParam(tabQuery);

  useEffect(() => {
    if (tabQuery && !isValidUserOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  const setTabInUrl = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tab");
        return next;
      },
      { replace: true },
    );
  };

  if (!user) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">This user could not be loaded.</p>
        </Col>
      </Row>
    );
  }

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <Row className="align-items-center g-3">
                      <div className="col-auto">
                        <div className="avatar-lg rounded-circle overflow-hidden">
                          <Avatar
                            name={`${user.firstName ?? ""} ${user.lastName ?? ""}`}
                            image={user.avatar}
                            className="fs-20 w-100 h-100 img-thumbnail"
                            lightbox
                          />
                        </div>
                      </div>

                      <div className="col-md">
                        <div>
                          <h4 className="fw-bold">
                            {user.firstName} {user.lastName}
                          </h4>

                          <div className="hstack gap-3 flex-wrap">
                            <div>
                              Create Date :{" "}
                              <span className="fw-medium">
                                <DateTime
                                  date={user.created_at ? user.created_at : ""}
                                />
                              </span>
                            </div>
                            <div className="vr"></div>
                          </div>
                        </div>
                      </div>
                    </Row>
                  </div>
                  {canEditUser ? (
                    <div className="col-md-auto">
                      <div className="hstack gap-2 flex-nowrap">
                        <Link
                          to={`/user-management/users/edit/${user.id}`}
                          className="btn btn-primary btn-sm"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      </div>
                    </div>
                  ) : null}
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl();
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
